import { apiGet, apiPost, apiPut, apiDelete } from '../api/apiClient';
import { API_ENDPOINTS } from '@/constants/endpoints';
import {
  KnowledgeBaseLink,
  KnowledgeBaseLinkListParams,
  KnowledgeBaseLinkListResponse,
  CreateKnowledgeBaseLinkRequest,
  UpdateKnowledgeBaseLinkRequest,
  KnowledgeBaseLinkActionResponse,
} from '@/constants/types/knowledgeBase.types';

class KnowledgeBaseService {
  /**
   * Fetch knowledge base links with pagination, search, and filters
   */
  async getLinks(
    params?: KnowledgeBaseLinkListParams
  ): Promise<KnowledgeBaseLinkListResponse> {
    try {
      const queryParams: Record<string, string> = {};

      if (params?.page) queryParams.page = params.page.toString();
      if (params?.limit) queryParams.limit = params.limit.toString();
      if (params?.search) queryParams.search = params.search;
      if (params?.sort) queryParams.sort = params.sort;
      if (params?.folderId) queryParams.folderId = params.folderId;

      const queryString = new URLSearchParams(queryParams).toString();
      const url = queryString
        ? `${API_ENDPOINTS.ADMIN.LINKS}?${queryString}`
        : API_ENDPOINTS.ADMIN.LINKS;

      return await apiGet<KnowledgeBaseLinkListResponse>(url);
    } catch (error: any) {
      throw error.response?.data || { message: 'Failed to fetch knowledge base links' };
    }
  }

  /**
   * Create a new knowledge base link
   */
  async createLink(
    data: CreateKnowledgeBaseLinkRequest
  ): Promise<KnowledgeBaseLinkActionResponse> {
    try {
      return await apiPost<KnowledgeBaseLinkActionResponse>(
        API_ENDPOINTS.ADMIN.LINKS,
        data
      );
    } catch (error: any) {
      throw error.response?.data || { message: 'Failed to create knowledge base link' };
    }
  }

  /**
   * Update an existing knowledge base link
   */
  async updateLink(
    id: string,
    data: UpdateKnowledgeBaseLinkRequest
  ): Promise<KnowledgeBaseLinkActionResponse> {
    try {
      return await apiPut<KnowledgeBaseLinkActionResponse>(
        API_ENDPOINTS.ADMIN.LINK_BY_ID(id),
        data
      );
    } catch (error: any) {
      throw error.response?.data || { message: 'Failed to update knowledge base link' };
    }
  }

  /**
   * Delete a knowledge base link
   */
  async deleteLink(id: string): Promise<KnowledgeBaseLinkActionResponse> {
    try {
      return await apiDelete<KnowledgeBaseLinkActionResponse>(
        API_ENDPOINTS.ADMIN.LINK_BY_ID(id)
      );
    } catch (error: any) {
      throw error.response?.data || { message: 'Failed to delete knowledge base link' };
    }
  }

  /**
   * Get a single link by ID
   */
  async getLinkById(
    id: string
  ): Promise<{ success: boolean; data: { link: KnowledgeBaseLink } }> {
    try {
      return await apiGet<{ success: boolean; data: { link: KnowledgeBaseLink } }>(
        API_ENDPOINTS.ADMIN.LINK_BY_ID(id)
      );
    } catch (error: any) {
      throw error.response?.data || { message: 'Failed to fetch link details' };
    }
  }
}

export const knowledgeBaseService = new KnowledgeBaseService();
